import React, { useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';

import { CreateWithoutTemplate, CreateWithTemplate } from '@candu-ai/content/create';
import { ContentType } from '@candu-ai/content/types';
import { Flags, useAuthentication, useFlags, useNavigation } from 'commons/hooks';
import { View } from 'dashboard/components/View';
import { contentPath } from 'dashboard/pages/Content/constants';
import NotFound from 'dashboard/pages/Content/pages/Create/NotFound';
import { SNAPSHOT_ROUTE } from 'dashboard/pages/Content/pages/Create/pages/Snapshot';

export const Name = () => {
  const { extensionVersion, applicationId } = useAuthentication();
  const { [Flags.MODALS]: newModals, [Flags.INLINE_SNAPSHOTS]: withInlineSnapshots } = useFlags();
  const navigate = useNavigation();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');
  const folderId = searchParams.get('folderId');
  const contentType = searchParams.get('contentType') as ContentType;
  const skipForm = searchParams.get('skipForm') === 'true';

  const withSnapshots = useMemo(
    () =>
      (withInlineSnapshots && contentType === ContentType.Inline) ||
      contentType === ContentType.Modal ||
      contentType === ContentType.HotspotGroup ||
      contentType === ContentType.Tour,
    [contentType, withInlineSnapshots],
  );

  useEffect(() => {
    if (!contentType) {
      navigate('../new');
    }
  }, [contentType]);

  const onSuccessCreate = useCallback(
    ({ id }: { id: string }) => {
      if (withSnapshots) {
        navigate(`../new/${id}/${SNAPSHOT_ROUTE.path}?contentType=${contentType}`);
      } else {
        navigate(contentPath(id));
      }
    },
    [navigate],
  );

  return (
    <View>
      <Helmet title="Create Content - Name | Candu" />
      {templateId ? (
        <CreateWithTemplate
          notFoundComponent={<NotFound />}
          context={{
            newModals,
            applicationId,
            contentType: contentType || ContentType.Inline,
            skipForm,
            withSnapshots,
            hasExtension: !!extensionVersion,
          }}
          templateId={templateId}
          folderId={folderId}
          onSuccessCreate={onSuccessCreate}
        />
      ) : (
        <CreateWithoutTemplate
          context={{
            newModals,
            contentType: contentType || ContentType.Inline,
            skipForm,
            withSnapshots,
            hasExtension: !!extensionVersion,
          }}
          folderId={folderId || undefined}
          onSuccessCreate={onSuccessCreate}
        />
      )}
    </View>
  );
};

export default Name;
